import { PaymentMethod } from '../domain/models';
import {
  calcCartTotalsFromRoundedTtc,
  cartLineTotalTtc,
  salePriceTtcFromHt,
} from '@incashflow/shared/utils';
import type { Receipt } from '../presentation/screens/cashierselling/CashierSellingViewModelState';
import {
  formatThermalReceiptDate,
  type ThermalReceiptData,
} from './thermalReceiptTypes';
import { normalizeQalamPaymentLabel } from '@incashflow/shared/receipt/qalamReceiptFormat';

const PAYMENT_LABELS: Record<PaymentMethod, string> = {
  [PaymentMethod.CASH]: 'ESPECE',
  [PaymentMethod.CARD]: 'CARTE',
  [PaymentMethod.CHEQUE]: 'CHEQUE',
};

function roundMillimes(n: number): number {
  return Math.round(n * 1000) / 1000;
}

export function mapSaleReceiptToThermal(
  receipt: Receipt,
  options: {
    branchName: string;
    taxAmount?: number;
    storeName?: string;
    registerNumber?: string;
    cashierLabel?: string;
  },
): ThermalReceiptData {
  const createdAtDate = formatThermalReceiptDate(receipt.createdAt);
  const totals = calcCartTotalsFromRoundedTtc(
    receipt.items,
    receipt.discountPercentage,
  );

  return {
    storeName: options.storeName ?? 'Qalam Shop',
    branchName: options.branchName,
    receiptId: receipt.receiptNumber,
    createdAt: createdAtDate.replace(' ', '  '),
    cashierName: receipt.cashierName,
    cashierLabel: options.cashierLabel,
    registerNumber: options.registerNumber ?? '1',
    lines: receipt.items.map((item) => {
      const tax = item.taxRate || 0;
      const unitTtc = salePriceTtcFromHt(item.unitPrice, tax);
      const grossTtc = roundMillimes(item.quantity * unitTtc);
      const lineTotalTtc = cartLineTotalTtc(item);
      const productDiscountTtc = roundMillimes(grossTtc - lineTotalTtc);

      return {
        name: item.productName,
        quantity: item.quantity,
        unitPriceTtc: unitTtc,
        lineTotalTtc: grossTtc,
        ...(productDiscountTtc > 0
          ? {
              productDiscountTtc,
              ...(item.productDiscountPercentage > 0
                ? { productDiscountPercentage: item.productDiscountPercentage }
                : {}),
            }
          : {}),
      };
    }),
    subtotal: receipt.subtotal,
    discountAmount: receipt.discountAmount,
    ...(totals.cartDiscountTtc > 0
      ? {
          cartDiscountAmount: totals.cartDiscountTtc,
          ...(receipt.discountPercentage > 0
            ? { cartDiscountPercentage: receipt.discountPercentage }
            : {}),
        }
      : {}),
    taxAmount: options.taxAmount ?? receipt.taxAmount,
    grandTotal: receipt.grandTotal,
    paymentMethod: normalizeQalamPaymentLabel(
      PAYMENT_LABELS[receipt.paymentMethod] ?? receipt.paymentMethod,
    ),
    cashGiven:
      receipt.paymentMethod === PaymentMethod.CASH ? receipt.cashGiven : undefined,
    cashReturned:
      receipt.paymentMethod === PaymentMethod.CASH
        ? receipt.cashReturned
        : undefined,
    currencyCode: 'TND',
  };
}
